import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_QUICK_LINKS, MAX_QUICK_LINKS, isQuickLinkable, resolveQuickLinks } from '../config/navigation';
import { getUserPreferences, saveQuickLinks, saveDashboardVersion, saveEmailNotifications } from '../services/userPreferenceService';
import { useAppConfig } from './AppConfigContext';

// The signed-in admin's own settings: their dashboard quick links, which landing page they see, and
// whether they receive broadcast admin emails.
// Shared between the sidebar (which offers pages to pin), the dashboard (which renders and removes
// them) and the layout (which hides the sidebar on the old dashboard). Edits apply straight away and
// save in the background; a failed save puts things back where the server still has them.

const UserPreferencesContext = createContext(null);

export const UserPreferencesProvider = ({ children }) => {
  const { config } = useAppConfig();
  const [paths, setPaths] = useState(DEFAULT_QUICK_LINKS);
  // null means "follow the org default"; the caller decides what that is.
  const [dashboardVersion, setDashboardVersionState] = useState(null);
  // Ticket 101: opting out only stops broadcast notices, so true is the right shape when it is unset.
  const [emailNotificationsEnabled, setEmailNotificationsState] = useState(true);
  const [ready, setReady] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [error, setError] = useState(null);

  const saveId = useRef(0);                          // latest-wins: a slow save must not undo a newer edit
  const savedPaths = useRef(DEFAULT_QUICK_LINKS);    // the last list the server confirmed
  const emailSaveId = useRef(0);                      // latest-wins for what the switch shows
  const emailConfirmedId = useRef(0);                // stops an out-of-order reply overwriting a newer one
  const savedEmailNotifications = useRef(true);      // the last value the server confirmed

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const prefs = await getUserPreferences();
        if (cancelled) return;
        // null means never customised; an empty array means every link was deliberately removed.
        const stored = Array.isArray(prefs?.dashboardQuickLinks) ? prefs.dashboardQuickLinks : null;
        const next = stored === null ? DEFAULT_QUICK_LINKS : stored.filter(isQuickLinkable);
        savedPaths.current = next;
        setPaths(next);
        setDashboardVersionState(prefs?.dashboardVersion ?? null);
        const emails = typeof prefs?.emailNotificationsEnabled === 'boolean' ? prefs.emailNotificationsEnabled : true;
        savedEmailNotifications.current = emails;
        setEmailNotificationsState(emails);
      } catch (err) {
        // Preferences are not worth blocking the dashboard for — fall back to the defaults.
        console.error('Failed to load user preferences:', err);
        if (!cancelled) setLoadFailed(true);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const persist = useCallback(async (next) => {
    const id = ++saveId.current;
    const previous = savedPaths.current;

    setPaths(next);
    setError(null);

    try {
      const result = await saveQuickLinks(next);
      if (id !== saveId.current) return;
      const confirmed = Array.isArray(result?.dashboardQuickLinks) ? result.dashboardQuickLinks : next;
      savedPaths.current = confirmed;
      setPaths(confirmed.filter(isQuickLinkable));
    } catch (err) {
      if (id !== saveId.current) return;
      setPaths(previous);
      setError(err.message || 'Could not save your quick links.');
    }
  }, []);

  // Switching dashboard reloads the page rather than swapping in place: the two use completely
  // different chrome (sidebar vs none), and a clean remount is less surprising than watching the
  // furniture rearrange itself around you.
  const setDashboardVersion = useCallback(async (version) => {
    const previous = dashboardVersion;
    setDashboardVersionState(version);
    try {
      await saveDashboardVersion(version);
      window.location.assign('/');
    } catch (err) {
      setDashboardVersionState(previous);
      setError(err.message || 'Could not switch dashboard.');
    }
  }, [dashboardVersion]);

  // Applies straight away and saves in the background. Returns whether the server took it, so the
  // screen can say so; a failed save puts the switch back where the server still has it.
  const setEmailNotifications = useCallback(async (enabled) => {
    const id = ++emailSaveId.current;

    setEmailNotificationsState(enabled);

    try {
      const result = await saveEmailNotifications(enabled);
      const confirmed = typeof result?.emailNotificationsEnabled === 'boolean' ? result.emailNotificationsEnabled : enabled;
      // Record it even when a newer toggle has taken over the switch: the server did accept this one.
      if (id > emailConfirmedId.current) {
        emailConfirmedId.current = id;
        savedEmailNotifications.current = confirmed;
      }
      if (id !== emailSaveId.current) return true;
      setEmailNotificationsState(confirmed);
      return true;
    } catch (err) {
      console.error('Failed to save email notification preference:', err);
      if (id !== emailSaveId.current) return false;
      setEmailNotificationsState(savedEmailNotifications.current);
      return false;
    }
  }, []);

  const value = useMemo(() => {
    const isPinned = (path) => paths.includes(path);
    const atCapacity = paths.length >= MAX_QUICK_LINKS;

    return {
      paths,
      links: resolveQuickLinks(paths, config),
      dashboardVersion,
      setDashboardVersion,
      emailNotificationsEnabled,
      setEmailNotifications,
      ready,
      loadFailed,
      error,
      atCapacity,
      isPinned,
      add: (path) => {
        if (!isQuickLinkable(path) || isPinned(path) || atCapacity) return;
        persist([...paths, path]);
      },
      remove: (path) => {
        if (!isPinned(path)) return;
        persist(paths.filter((p) => p !== path));
      },
      reset: () => persist(DEFAULT_QUICK_LINKS),
      dismissError: () => setError(null),
    };
  }, [paths, dashboardVersion, setDashboardVersion, emailNotificationsEnabled, setEmailNotifications, ready, loadFailed, error, persist, config]);

  return <UserPreferencesContext.Provider value={value}>{children}</UserPreferencesContext.Provider>;
};

export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);
  if (!context) throw new Error('useUserPreferences must be used inside a UserPreferencesProvider.');
  return context;
};

// Kept as the name the quick-links components already use, so they read as being about quick links.
export const useQuickLinks = useUserPreferences;
